// ─── StreetViewThumbnail — preview com fallback satélite ────────────────
//
// A Static Street View API devolve sempre uma imagem (status HTTP 200),
// mesmo quando não há cobertura — nesse caso a imagem é cinzenta com
// "Sorry, we have no imagery here". Resultado: `<img onError>` nunca
// dispara e o utilizador vê a imagem em branco.
//
// Este componente usa a Street View Metadata API primeiro para verificar
// se há cobertura na localização. Em caso negativo (típico em Angola,
// zonas rurais), instancia um google.maps.Map mini em modo híbrido
// (satélite + estradas) zoom 18 com marcador no veículo. Não usa a
// Static Maps API porque requer um produto separado activado no Cloud
// Console — em vez disso usa a Maps JavaScript API que já está activa
// para o mapa principal funcionar.
//
// Cache local por coordenadas arredondadas (4 casas decimais ~ 11m) para
// evitar repetidas chamadas à Metadata API.

import React, { useEffect, useState, useMemo, useRef } from 'react';

const metadataCache = new Map(); // key: 'lat,lng' rounded → 'sv' | 'sat'

function cacheKey(lat, lng) {
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

export default function StreetViewThumbnail({
  lat,
  lng,
  heading = 0,
  apiKey,
  width = 400,
  height = 180,
  style = {},
  satelliteZoom = 18,
}) {
  const key = useMemo(() => cacheKey(lat, lng), [lat, lng]);
  const [mode, setMode] = useState(() => metadataCache.get(key) || null);
  const satRef = useRef(null);
  const satMapRef = useRef(null);

  // Metadata fetch (1x por coordenada arredondada)
  useEffect(() => {
    if (mode) return;
    let cancelled = false;
    const url = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lng}&key=${apiKey}`;
    fetch(url)
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const next = json?.status === 'OK' ? 'sv' : 'sat';
        metadataCache.set(key, next);
        setMode(next);
      })
      .catch(() => {
        if (cancelled) return;
        metadataCache.set(key, 'sat');
        setMode('sat');
      });
    return () => { cancelled = true; };
  }, [lat, lng, apiKey, key, mode]);

  // Quando o modo é satélite, instancia um google.maps.Map no satRef.
  // Usa a Maps JavaScript API (já carregada para o mapa principal) em vez
  // da Static Maps API (que pode não estar activa no projecto Google).
  useEffect(() => {
    if (mode !== 'sat') return;
    if (!satRef.current) return;
    if (!window.google?.maps) return;
    if (satMapRef.current) {
      satMapRef.current.setCenter({ lat, lng });
      return;
    }
    satMapRef.current = new window.google.maps.Map(satRef.current, {
      center: { lat, lng },
      zoom: satelliteZoom,
      mapTypeId: 'hybrid',
      disableDefaultUI: true,
      gestureHandling: 'none', // thumbnail não-interactivo
      clickableIcons: false,
      keyboardShortcuts: false,
    });
    new window.google.maps.Marker({ position: { lat, lng }, map: satMapRef.current });
  }, [mode, lat, lng, satelliteZoom]);

  // Reset quando muda de localização
  useEffect(() => {
    satMapRef.current = null;
  }, [lat, lng]);

  // Loading state
  if (!mode) {
    return (
      <div style={{
        width: '100%', height: '100%',
        background: 'linear-gradient(110deg, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%)',
        backgroundSize: '200% 100%',
        animation: 'streetview-skeleton 1.4s linear infinite',
        ...style,
      }}>
        <style>{`@keyframes streetview-skeleton{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
      </div>
    );
  }

  // Street View image — usa Static Street View (gratuita, sem necessitar
  // de produto adicional activado no projecto Google).
  if (mode === 'sv') {
    const src = `https://maps.googleapis.com/maps/api/streetview?size=${width}x${height}&location=${lat},${lng}&heading=${Math.round(heading)}&fov=90&pitch=0&key=${apiKey}`;
    return (
      <img
        src={src}
        alt="Street View"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...style }}
        onError={() => {
          // Defensivo: se a imagem em si falhar, comuta para satélite via JS
          metadataCache.set(key, 'sat');
          setMode('sat');
        }}
      />
    );
  }

  // Satélite via Maps JavaScript API
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <div ref={satRef} style={{ width: '100%', height: '100%' }} />
      <div style={{
        position: 'absolute', top: 6, left: 6,
        background: 'rgba(15,23,42,0.85)', color: '#fff',
        padding: '3px 9px', borderRadius: 6,
        fontSize: 10.5, fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 4,
        backdropFilter: 'blur(4px)',
        zIndex: 1,
        pointerEvents: 'none',
      }}>
        🛰️ Satélite
      </div>
    </div>
  );
}
